import {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

interface UseIsOverflowingResult<
  TContainer extends HTMLElement,
  TContent extends HTMLElement,
> {
  containerRef: RefObject<TContainer | null>;
  contentRef: RefObject<TContent | null>;
  isOverflowing: boolean;
}

export function useIsOverflowing<
  TContainer extends HTMLElement,
  TContent extends HTMLElement = TContainer,
>(content: unknown): UseIsOverflowingResult<TContainer, TContent> {
  const containerRef = useRef<TContainer | null>(null);
  const contentRef = useRef<TContent | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useLayoutEffect(() => {
    const el = contentRef.current;

    if (el) {
      setIsOverflowing(el.scrollWidth > el.clientWidth);
    }
  }, [content]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const observer = new ResizeObserver(() => {
      const el = contentRef.current;

      if (el) {
        setIsOverflowing(el.scrollWidth > el.clientWidth);
      }
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  return { containerRef, contentRef, isOverflowing };
}
